/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import ChevronLeft from '@spectrum-icons/workflow/ChevronLeft';
import {Dialog, Text} from '@adobe/react-spectrum';
import {IntlProvider} from '@quarry/intl';
import * as localeDataShellOrgSwitcherPopover from './__localization__/ShellOrgSwitcherPopover';
import React, {lazy, Suspense, useRef} from 'react';
import ShellWait from './ShellWait.tsx';
import {useIntl} from 'react-intl';
import './ShellOrgSwitcherPopover.scss';

const ShellOrgSwitcher = lazy(() => import('./ShellOrgSwitcher'));

const OrgSwitcher = ({locale, ...otherProps}) => (
  <Suspense fallback={<ShellWait locale={locale} size="M" />}>
    <ShellOrgSwitcher locale={locale} {...otherProps} />
  </Suspense>
);

export default function ShellOrgSwitcherPopover({hasAccountClusterData, hasSandboxBar, locale, ...otherProps}) {
  // ...otherProps is listed before set parameters because in this case the current minifier
  // combines the prop objects in such a way that className is always '' if otherProps is last
  return (
    <Dialog
      {...otherProps}
      UNSAFE_className={`spectrum-Shell-orgSwitcher ${hasSandboxBar ? 'hasSandboxBar' : ''}`}
      isDismissable={false}
    >
      {hasAccountClusterData ? <OrgSwitcher locale={locale} {...otherProps} /> : <ShellWait locale={locale} size="M" />}
    </Dialog>
  );
}

export function I18nShellOrgSwitcherScreen(props) {
  const {locale} = props;
  return (
    <IntlProvider locale={locale} messages={localeDataShellOrgSwitcherPopover}>
      <ShellOrgSwitcherScreen {...props} />
    </IntlProvider>
  );
}

export function ShellOrgSwitcherScreen({
  hasAccountClusterData,
  locale,
  onOrgSwitcherClick,
  orgSwitcherScreenTabIndex,
  orgSwitcherActive,
  ...otherProps
}) {
  const backButton = useRef(null);
  const {formatMessage} = useIntl();

  return (
    <div className={`spectrum-Shell-orgSwitcherScreen ${orgSwitcherActive ? 'active' : ''}`}>
      <div className="spectrum-Shell-orgSwitcherScreenHeading">
        <button
          className="spectrum-Shell-orgSwitcherScreenButton-back"
          onClick={() => onOrgSwitcherClick(false)}
          ref={backButton}
          tabIndex={orgSwitcherScreenTabIndex}
          type="button"
        >
          <ChevronLeft UNSAFE_className="spectrum-Shell-orgSwitcherIcon-back" size="S" />
        </button>
        <Text UNSAFE_className="spectrum-Menu-sectionHeading">
          {formatMessage({
            defaultMessage: 'Organizations',
            description: 'Menu header for organizations',
            id: 'organizationsHeading'
          })}
        </Text>
      </div>
      {hasAccountClusterData ? (
        <OrgSwitcher inProfile locale={locale} orgSwitcherScreenTabIndex={orgSwitcherScreenTabIndex} {...otherProps} />
      ) : (
        <ShellWait locale={locale} size="M" />
      )}
    </div>
  );
}
